import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPublicPrograms, getCompletedWorkouts } from '../utils/workoutService';
import { describeRun } from '../utils/runWorkout';

/**
 * WorkoutLibraryNew - Complete workout program flow
 * Program Selection → 4-Week Overview → Week View → Workout Tracker
 */

const WorkoutLibraryNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Navigation state
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  
  // Always scroll to top when changing major view state
  useEffect(() => {
    if (selectedProgram !== null) {
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}
    }
  }, [selectedProgram]);
  useEffect(() => {
    if (selectedWeek !== null) {
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}
    }
  }, [selectedWeek]);

  // Track completed workouts - fetched from Supabase with program update date filtering
  const [completedWorkouts, setCompletedWorkouts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
    } catch {
      return {};
    }
  });
  
  // Fetch completed workouts from Supabase on mount and MERGE with localStorage
  // This prevents losing completions if a Supabase save failed
  useEffect(() => {
    const fetchCompletedWorkouts = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) return;
      
      try {
        const result = await getCompletedWorkouts(userEmail);
        if (result.success) {
          // Merge: localStorage completions take priority over Supabase
          // so we never lose a workout that was saved locally but failed to reach Supabase
          let localData = {};
          try {
            localData = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
          } catch {}
          setCompletedWorkouts({ ...result.data, ...localData });
        }
      } catch (error) {
        console.error('Failed to fetch completed workouts:', error);
      }
    };
    
    fetchCompletedWorkouts();
  }, []);
  
  // Listen for workout completion events to refresh stats in real-time
  useEffect(() => {
    const handleWorkoutCompleted = async () => {
      console.log('🔄 Workout completed - refreshing program stats...');
      // Re-fetch from Supabase and merge with localStorage
      const userEmail = localStorage.getItem('userEmail');
      let localData = {};
      try {
        localData = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
      } catch {}
      
      if (userEmail) {
        try {
          const result = await getCompletedWorkouts(userEmail);
          if (result.success) {
            // Merge: local completions take priority so nothing is lost
            setCompletedWorkouts({ ...result.data, ...localData });
            return;
          }
        } catch {}
      }
      // Fallback to localStorage only
      setCompletedWorkouts(localData);
    };
    
    window.addEventListener('workoutCompleted', handleWorkoutCompleted);
    
    return () => {
      window.removeEventListener('workoutCompleted', handleWorkoutCompleted);
    };
  }, []);

  // Calculate stats from completed workouts
  const calculateStats = () => {
    const workouts = Object.values(completedWorkouts);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const thisWeek = workouts.filter(w => new Date(w.completedAt) >= weekAgo).length;
    const thisMonth = workouts.filter(w => new Date(w.completedAt) >= monthAgo).length;
    const totalMinutes = workouts.reduce((sum, w) => sum + (w.duration || 0), 0);

    return {
      thisWeek,
      thisMonth,
      totalWorkouts: workouts.length,
      totalMinutes: Math.floor(totalMinutes / 60)
    };
  };

  // Calculate program progress (only counts completions AFTER program was last updated)
  const calculateProgramProgress = (program) => {
    if (!program.weeks) return 0;
    
    const totalWorkouts = program.weeks.reduce((sum, week) => 
      sum + (week.workouts?.length || 0), 0
    );
    
    const programUpdatedAt = program.updatedAt ? new Date(program.updatedAt) : null;
    
    const completedCount = program.weeks.reduce((sum, week) => {
      const weekCompleted = week.workouts?.filter(w => {
        const record = completedWorkouts[w.id];
        if (!record) return false;
        // Only count if completion happened after program was last updated
        if (programUpdatedAt && record.completedAt) {
          return new Date(record.completedAt) > programUpdatedAt;
        }
        return true;
      }).length || 0;
      return sum + weekCompleted;
    }, 0);

    return totalWorkouts > 0 ? Math.round((completedCount / totalWorkouts) * 100) : 0;
  };

  const stats = calculateStats();

  // Auto-map admin programs to member view format
  const mapAdminProgramToMemberView = (adminProgram) => {
    // Color options for programs
    const colorOptions = [
      'from-pink-400 to-rose-500',
      'from-purple-400 to-indigo-500',
      'from-teal-400 to-cyan-500',
      'from-orange-400 to-red-500',
      'from-blue-400 to-indigo-500',
      'from-green-400 to-emerald-500'
    ];

    // Calculate workouts per week from frequency or first week
    let workoutsPerWeek = 4;
    if (adminProgram.frequency) {
      // Extract number from "4x/week" format
      const match = adminProgram.frequency.match(/(\d+)/);
      workoutsPerWeek = match ? parseInt(match[1]) : 4;
    } else {
      const firstWeek = adminProgram.weeks?.[0];
      workoutsPerWeek = firstWeek?.workouts?.length || 4;
    }

    // Use equipment from admin or default
    const equipment = adminProgram.equipment || 
      (adminProgram.level === 'Beginner' ? 'Minimal equipment' : 'Full gym access');

    // Generate week focus if not provided
    const weekFocusDefaults = [
      'Foundation & Form - Building the basics',
      'Progressive Overload - Increasing intensity',
      'Peak Performance - Maximum effort',
      'Deload & Recovery - Active rest week'
    ];

    return {
      id: adminProgram.id || adminProgram.title?.toLowerCase().replace(/\s+/g, '-'),
      name: adminProgram.title,
      description: adminProgram.description,
      duration: adminProgram.duration || '4 weeks',
      workoutsPerWeek: workoutsPerWeek,
      level: adminProgram.level || 'Intermediate',
      equipment: equipment,
      color: adminProgram.color || colorOptions[Math.floor(Math.random() * colorOptions.length)],
      image: adminProgram.icon || '💪',
      imageUrl: adminProgram.image_url || adminProgram.imageUrl || null,
      // Completions only count if logged after progress_reset_at. This bumps
      // when a workout is removed/replaced — NOT on text edits, so fixing
      // mistakes no longer wipes member progress. Falls back to updated_at
      // for programs saved before the column existed.
      updatedAt: adminProgram.progress_reset_at || adminProgram.updated_at || adminProgram.created_at || null,
      weeks: (adminProgram.weeks || []).map((week, idx) => ({
        weekNumber: week.week || idx + 1,
        focus: week.focus || weekFocusDefaults[idx] || 'Training week',
        workouts: week.workouts || []
      }))
    };
  };
  
  // Load programs from Supabase
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await getPublicPrograms();
        console.log('🔍 WorkoutLibraryNew: getPublicPrograms result:', result);
        
        if (result.success && result.data) {
          console.log('📊 Total programs from DB:', result.data.length);
          console.log('📊 Program statuses:', result.data.map(p => ({ title: p.title, status: p.status })));
          
          // Filter to ONLY show programs explicitly marked as 'active'
          const activePrograms = result.data.filter(p => p.status === 'active');
          console.log('✅ Active programs after filter:', activePrograms.length);
          
          const mappedPrograms = activePrograms.map(mapAdminProgramToMemberView);
          console.log('🎯 Mapped programs:', mappedPrograms.length);
          
          setPrograms(mappedPrograms.length === 0 ? samplePrograms : mappedPrograms);
        } else {
          console.log('❌ No programs returned from DB, using samples');
          setPrograms(samplePrograms);
        }
      } catch (error) {
        console.error('❌ Error loading programs:', error);
        setPrograms(samplePrograms);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  
  // Handle return from workout completion
  useEffect(() => {
    if (location.state?.returnToProgram && location.state?.returnToWeek) {
      const program = programs.find(p => p.id === location.state.returnToProgram);
      if (program) {
        setSelectedProgram(program);
        setSelectedWeek(location.state.returnToWeek);
      }
    }
  }, [location.state, programs]);

  // Sample Programs Data (fallback)
  const samplePrograms = [
    {
      id: 'gym-girlie',
      name: 'Gym Girlie',
      description: 'Build strength and confidence in the gym with structured progressive overload',
      duration: '4 weeks',
      workoutsPerWeek: 4,
      level: 'Intermediate',
      equipment: 'Full gym access',
      color: 'from-pink-400 to-rose-500',
      image: '💪',
      weeks: generateSampleWeeks('Gym Girlie')
    },
    {
      id: 'working-mum',
      name: 'Working Mum',
      description: 'Efficient 30-minute workouts that fit into your busy schedule',
      duration: '4 weeks',
      workoutsPerWeek: 3,
      level: 'Beginner to Intermediate',
      equipment: 'Minimal equipment',
      color: 'from-purple-400 to-indigo-500',
      image: '🏃‍♀️',
      weeks: generateSampleWeeks('Working Mum')
    },
    {
      id: 'at-home',
      name: 'At Home',
      description: 'No gym? No problem. Effective workouts using bodyweight and minimal equipment',
      duration: '4 weeks',
      workoutsPerWeek: 4,
      level: 'All levels',
      equipment: 'Bodyweight, resistance bands',
      color: 'from-teal-400 to-cyan-500',
      image: '🏠',
      weeks: generateSampleWeeks('At Home')
    },
    {
      id: 'strength-builder',
      name: 'Strength Builder',
      description: 'Focus on compound movements and progressive strength gains',
      duration: '4 weeks',
      workoutsPerWeek: 4,
      level: 'Intermediate to Advanced',
      equipment: 'Full gym access',
      color: 'from-orange-400 to-red-500',
      image: '🔥',
      weeks: generateSampleWeeks('Strength Builder')
    }
  ];

  // Reset to program selection
  const resetToPrograms = () => {
    setSelectedProgram(null);
    setSelectedWeek(null);
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}
  };

  // Select a program
  const selectProgram = (program) => {
    setSelectedProgram(program);
    setSelectedWeek(null);
  };

  // Select a week
  const selectWeek = (weekNumber) => {
    setSelectedWeek(weekNumber);
  };

  // Start a workout
  const startWorkout = (workout) => {
    // Navigate to WorkoutTrackerV2 with workout data and return info
    navigate('/workouts-v2', { 
      state: { 
        workout,
        programId: selectedProgram?.id,
        weekNumber: selectedWeek
      } 
    });
  };

  // Render program selection
  if (!selectedProgram) {
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mx-auto mb-4"></div>
            <p className="text-charcoal-600">Loading programs...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-4xl font-bold text-charcoal-800">Workout Programs</h1>
              </div>
            </div>
          </div>

          {/* Subtitle */}
          <div className="text-center mb-8">
            <p className="text-charcoal-600 text-lg">
              Choose a program that fits your lifestyle and goals
            </p>
          </div>

          {/* Program Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {programs.map((program) => {
              const progress = calculateProgramProgress(program);
              return (
                <motion.div
                  key={program.id}
                  onClick={() => selectProgram(program)}
                  className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-coral-400 cursor-pointer transition-all"
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="mb-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-2xl font-bold text-charcoal-800">
                        {program.name}
                      </h3>
                      {progress > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex-shrink-0">
                          {progress}%
                        </span>
                      )}
                    </div>
                    <p className="text-charcoal-600 text-sm leading-relaxed mt-3">
                      {program.description}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  {progress > 0 && (
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                  <div>
                    <div className="text-xs text-charcoal-500 uppercase tracking-wide mb-1">Duration</div>
                    <div className="text-sm font-semibold text-charcoal-800">{program.duration}</div>
                  </div>
                  <div>
                    <div className="text-xs text-charcoal-500 uppercase tracking-wide mb-1">Frequency</div>
                    <div className="text-sm font-semibold text-charcoal-800">{program.workoutsPerWeek}x/week</div>
                  </div>
                  <div>
                    <div className="text-xs text-charcoal-500 uppercase tracking-wide mb-1">Level</div>
                    <div className="text-sm font-semibold text-charcoal-800">{program.level}</div>
                  </div>
                  <div>
                    <div className="text-xs text-charcoal-500 uppercase tracking-wide mb-1">Equipment</div>
                    <div className="text-sm font-semibold text-charcoal-800">{program.equipment}</div>
                  </div>
                </div>

                <button className="w-full mt-4 bg-gradient-to-r from-rose-400 to-pink-400 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
                  Start Program →
                </button>
              </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Render program with weeks (combined view)
  if (selectedProgram && !selectedWeek) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <button
            onClick={resetToPrograms}
            className="text-charcoal-600 hover:text-charcoal-800 mb-8 flex items-center gap-2 text-sm font-medium"
          >
            ← Back to Programs
          </button>

          {/* Program Header */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 mb-8">
            <div className="mb-4">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <h1 className="text-3xl font-bold text-charcoal-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {selectedProgram.name}
                  </h1>
                </div>
              </div>
              <p className="text-charcoal-600 mt-3 mb-4 leading-relaxed">
                {selectedProgram.description}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-coral-500">📅</span>
                <span className="text-charcoal-700 font-medium">{selectedProgram.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-coral-500">💪</span>
                <span className="text-charcoal-700 font-medium">{selectedProgram.workoutsPerWeek}x per week</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-coral-500">📊</span>
                <span className="text-charcoal-700 font-medium">{selectedProgram.level}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-coral-500">🏋️</span>
                <span className="text-charcoal-700 font-medium">{selectedProgram.equipment}</span>
              </div>
            </div>
          </div>

          {/* Week Selection */}
          <div>
            <h2 className="text-2xl font-bold text-charcoal-800 mb-6">Choose Your Week</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((weekNum) => {
                const weekData = selectedProgram.weeks[weekNum - 1];
                return (
                  <motion.div
                    key={weekNum}
                    onClick={() => selectWeek(weekNum)}
                    className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-200 hover:border-coral-400 cursor-pointer transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-charcoal-800">
                        Week {weekNum}
                      </h3>
                      <span className="px-2.5 py-1 bg-coral-100 text-coral-700 rounded-full text-xs font-semibold">
                        {weekData.workouts.length} workouts
                      </span>
                    </div>
                    
                    <p className="text-charcoal-600 text-sm mb-3">
                      {weekData.focus}
                    </p>

                    <div className="flex items-center justify-between text-xs text-charcoal-500">
                      <span>Click to view workouts</span>
                      <span>→</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render week detail with individual workouts
  if (selectedProgram && selectedWeek) {
    const weekData = selectedProgram.weeks[selectedWeek - 1];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <button
            onClick={() => setSelectedWeek(null)}
            className="text-charcoal-600 hover:text-charcoal-800 mb-6 flex items-center gap-2"
          >
            ← Back to {selectedProgram.name}
          </button>

          <div className="mb-12">
            <h1 className="text-4xl font-bold text-charcoal-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Week {selectedWeek}
            </h1>
            <p className="text-charcoal-600 text-lg">
              {weekData.focus}
            </p>
          </div>

          {/* Workout Cards */}
          <div className="space-y-6 mb-12">
            {weekData.workouts.map((workout, idx) => {
              // Only show as completed if the completion happened AFTER the program was last updated
              // This prevents stale completions from showing when admin updates program content
              const completionRecord = completedWorkouts[workout.id];
              const isCompleted = completionRecord && (!selectedProgram.updatedAt || 
                new Date(completionRecord.completedAt) > new Date(selectedProgram.updatedAt))
                ? completionRecord : null;
              return (
                <motion.div
                  key={idx}
                  className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                    isCompleted 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-200 hover:border-coral-400'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-2xl font-bold text-charcoal-800">
                        {workout.title}
                      </h3>
                      {isCompleted && (
                        <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-semibold">
                          ✓ Completed
                        </span>
                      )}
                    </div>
                    <p className="text-charcoal-600 mb-4">
                      {workout.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-charcoal-500">
                      <span>⏱️ {workout.duration}</span>
                      <span>•</span>
                      {workout.workoutType === 'run' ? (
                        <span>🏃 {describeRun(workout)}</span>
                      ) : (
                        <span>💪 {workout.exercises?.length || 5} exercises</span>
                      )}
                      <span>•</span>
                      <span>🔥 {workout.difficulty}</span>
                    </div>
                    {isCompleted && (
                      <div className="mt-3 text-xs text-green-700">
                        Last completed: {new Date(isCompleted.completedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => startWorkout(workout)}
                    className={`w-full py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all ${
                      isCompleted
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gradient-to-r from-rose-400 to-pink-400 text-white hover:from-rose-500 hover:to-pink-500'
                    }`}
                  >
                    <span>{isCompleted ? 'Do Again' : workout.workoutType === 'run' ? 'Start Run' : 'Start Workout'}</span>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Helper function to generate sample weeks
function generateSampleWeeks(programName) {
  return [
    {
      weekNumber: 1,
      focus: 'Foundation & Form - Building the basics',
      workouts: [
        {
          id: 'w1d1',
          title: 'Day 1 – Full Body Strength',
          description: 'Compound movements to build overall strength',
          duration: '45 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Full Body')
        },
        {
          id: 'w1d2',
          title: 'Day 2 – Lower Body Focus',
          description: 'Glutes, quads, and hamstrings',
          duration: '50 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Lower Body')
        },
        {
          id: 'w1d3',
          title: 'Day 3 – Upper Body & Core',
          description: 'Build upper body strength and core stability',
          duration: '40 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Upper Body')
        },
        {
          id: 'w1d4',
          title: 'Day 4 – Active Recovery',
          description: 'Light cardio and mobility work',
          duration: '30 min',
          difficulty: 'Easy',
          exercises: generateSampleExercises('Recovery')
        }
      ]
    },
    {
      weekNumber: 2,
      focus: 'Progressive Overload - Increasing intensity',
      workouts: [
        {
          id: 'w2d1',
          title: 'Day 1 – Strength & Power',
          description: 'Heavy compound lifts',
          duration: '50 min',
          difficulty: 'Challenging',
          exercises: generateSampleExercises('Strength')
        },
        {
          id: 'w2d2',
          title: 'Day 2 – Glute Builder',
          description: 'Targeted glute activation and growth',
          duration: '45 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Glutes')
        },
        {
          id: 'w2d3',
          title: 'Day 3 – Push & Pull',
          description: 'Upper body strength balance',
          duration: '45 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Push Pull')
        },
        {
          id: 'w2d4',
          title: 'Day 4 – Conditioning',
          description: 'HIIT and metabolic conditioning',
          duration: '35 min',
          difficulty: 'Challenging',
          exercises: generateSampleExercises('HIIT')
        }
      ]
    },
    {
      weekNumber: 3,
      focus: 'Peak Performance - Maximum effort',
      workouts: [
        {
          id: 'w3d1',
          title: 'Day 1 – Heavy Lower',
          description: 'Max strength lower body',
          duration: '55 min',
          difficulty: 'Challenging',
          exercises: generateSampleExercises('Heavy Lower')
        },
        {
          id: 'w3d2',
          title: 'Day 2 – Upper Body Hypertrophy',
          description: 'Muscle building focus',
          duration: '50 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Hypertrophy')
        },
        {
          id: 'w3d3',
          title: 'Day 3 – Full Body Circuit',
          description: 'High intensity full body work',
          duration: '40 min',
          difficulty: 'Challenging',
          exercises: generateSampleExercises('Circuit')
        },
        {
          id: 'w3d4',
          title: 'Day 4 – Core & Stability',
          description: 'Deep core and balance work',
          duration: '35 min',
          difficulty: 'Moderate',
          exercises: generateSampleExercises('Core')
        }
      ]
    },
    {
      weekNumber: 4,
      focus: 'Deload & Recovery - Active rest week',
      workouts: [
        {
          id: 'w4d1',
          title: 'Day 1 – Light Full Body',
          description: 'Reduced volume, focus on form',
          duration: '40 min',
          difficulty: 'Easy',
          exercises: generateSampleExercises('Deload')
        },
        {
          id: 'w4d2',
          title: 'Day 2 – Mobility & Stretch',
          description: 'Deep stretching and mobility',
          duration: '30 min',
          difficulty: 'Easy',
          exercises: generateSampleExercises('Mobility')
        },
        {
          id: 'w4d3',
          title: 'Day 3 – Light Cardio',
          description: 'Low impact cardio session',
          duration: '30 min',
          difficulty: 'Easy',
          exercises: generateSampleExercises('Cardio')
        }
      ]
    }
  ];
}

// Helper to generate sample exercises
function generateSampleExercises(type) {
  const exerciseTemplates = {
    'Full Body': [
      { id: 'ex1', name: 'Barbell Squat', sets: [{ setNumber: 1, reps: '10', type: 'warmup', previousWeight: 95 }, { setNumber: 2, reps: '8', type: 'normal', previousWeight: 135 }, { setNumber: 3, reps: '8', type: 'normal', previousWeight: 135 }, { setNumber: 4, reps: '8', type: 'normal', previousWeight: 135 }], restTime: '90s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Keep chest up, drive through heels', muscleGroups: ['Quads', 'Glutes'] },
      { id: 'ex2', name: 'Bench Press', sets: [{ setNumber: 1, reps: '10', type: 'warmup', previousWeight: 45 }, { setNumber: 2, reps: '8', type: 'normal', previousWeight: 65 }, { setNumber: 3, reps: '8', type: 'normal', previousWeight: 65 }, { setNumber: 4, reps: '8', type: 'normal', previousWeight: 65 }], restTime: '90s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Lower to chest, press up explosively', muscleGroups: ['Chest', 'Triceps'] },
      { id: 'ex3', name: 'Deadlift', sets: [{ setNumber: 1, reps: '8', type: 'normal', previousWeight: 135 }, { setNumber: 2, reps: '8', type: 'normal', previousWeight: 135 }, { setNumber: 3, reps: '8', type: 'normal', previousWeight: 135 }], restTime: '120s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Hinge at hips, keep back straight', muscleGroups: ['Back', 'Hamstrings'] },
      { id: 'ex4', name: 'Plank Hold', sets: [{ setNumber: 1, reps: '60s', type: 'normal', isTimed: true }], restTime: '60s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Hold strong plank position', muscleGroups: ['Core'] }
    ],
    'Lower Body': [
      { id: 'ex1', name: 'Hip Thrust', sets: [{ setNumber: 1, reps: '10', type: 'warmup', previousWeight: 95 }, { setNumber: 2, reps: '10', type: 'normal', previousWeight: 135 }, { setNumber: 3, reps: '10', type: 'normal', previousWeight: 135 }, { setNumber: 4, reps: '10', type: 'dropset', previousWeight: 135 }], restTime: '90s', supersetGroup: 'A', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Drive through heels, squeeze glutes', muscleGroups: ['Glutes'] },
      { id: 'ex2', name: 'Bulgarian Split Squat', sets: [{ setNumber: 1, reps: '12', type: 'normal', previousWeight: 25 }, { setNumber: 2, reps: '12', type: 'normal', previousWeight: 25 }, { setNumber: 3, reps: '12', type: 'normal', previousWeight: 25 }], restTime: '60s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Keep torso upright, lower controlled', muscleGroups: ['Quads', 'Glutes'] },
      { id: 'ex3', name: 'Romanian Deadlift', sets: [{ setNumber: 1, reps: '12', type: 'normal', previousWeight: 95 }, { setNumber: 2, reps: '12', type: 'normal', previousWeight: 95 }, { setNumber: 3, reps: '12', type: 'failure', previousWeight: 95 }], restTime: '60s', videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', instructions: 'Feel stretch in hamstrings', muscleGroups: ['Hamstrings'] }
    ]
  };

  return exerciseTemplates[type] || exerciseTemplates['Full Body'];
}

export default WorkoutLibraryNew;
